import type { SettingEnumOption } from "../../../models";

import { enumValue } from "../settingForm";
import style from "./style.module.scss";

interface Props {
	value?: string | null;
	/** For an Enum setting, its definition's options */
	enumOptions?: SettingEnumOption[] | null;
	/** Longer text is cut off, with the full text in its tooltip */
	charLimit?: number;
	/** The value comes from a secret setting, which the server sends without its value */
	masked?: boolean;
}

/**
 * A setting value as people read it: an Enum value shows its option's label, with the stored value in its tooltip,
 * and is marked when it matches no option. A secret's value is hidden. Other values show as they are.
 */
export function ValueText({ value, enumOptions, charLimit, masked }: Props) {
	if (masked) return <span title="This setting is a secret, so its value isn't shown">••••••••</span>;

	const options = enumOptions ?? [];
	const shown = enumValue(value, options);

	if (shown?.matched === false)
		return (
			<span title={`Not one of the options: ${options.map((o) => o.label).join(", ")}`}>
				{value} <span className={style.unmatchedBadge}>Not an option</span>
			</span>
		);

	const text = shown?.label ?? value ?? "";
	const cut = charLimit && text.length > charLimit ? `${text.slice(0, charLimit)}[...]` : text;
	const title = shown ? `Stored value: ${value}` : cut !== text ? text : undefined;
	return <span title={title}>{cut}</span>;
}
